import { useState } from "react";
import useAppStore from "src/stores/appStore";
import {
  assistantIdSelector,
  isReadOnlyModeSelector,
  pageNameSelector,
} from "src/stores/selectors/appSelectors";
import {
  pollPreviewStatusSelector,
  previewDraftBuildResponseSelector
} from "src/stores/selectors/previewSelectors";
import {
  hasValidationErrors,
  isUpdatingCollectionSelector,
  setDialogErrorSelector,
  validationResultsSelector
} from "src/stores/selectors/collectionsSelectors";
import { setInfoPopoversSelector } from "src/stores/selectors/tripletsSelectors";
import { useDraft } from "src/queries/hooks/assistants";
import { deleteWidgetCookie } from "@utils";
import { BUILD_STATUSES, WIDGET_TOKEN } from "@consts";
import { DONT_SHOW_AGAIN_STORAGE } from "../header-types/status-header/DraftPublishing/DraftHeaderConsts";
import { post } from "src/services/api";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { queryClient } from "src/lib/react-query/queryClient";
import { getValidationsByType } from "src/components/collections/helpers";
import { VALIDATION_TYPES } from "src/components/collections/consts";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import { BUILD_TOOLTIP } from "./BuildButton.consts";

const useBuildButton = () => {
  const [fetchingStatus, setFetchingStatus] = useState(false);
  const buildDraftResponse = useAppStore(previewDraftBuildResponseSelector);
  const setBuildDraftResponse = useAppStore(state => state.setBuildDraftResponse);
  const selectedAssistantId = useAppStore(assistantIdSelector);
  const pageName = useAppStore(pageNameSelector);
  const pollBuildStatus = useAppStore(pollPreviewStatusSelector);
  const hasAssistantValidationErrors = useAppStore(hasValidationErrors);
  const isUpdatingCollection = useAppStore(isUpdatingCollectionSelector);
  const isReadOnly = useAppStore(isReadOnlyModeSelector);
  const setInfoPopovers = useAppStore(setInfoPopoversSelector);
  const setDialogError = useAppStore(setDialogErrorSelector);
  const validationResults = useAppStore(validationResultsSelector);
  const accountId = useAppStore(state => state.selectedAccountId);
  const unassigned = getValidationsByType(validationResults, VALIDATION_TYPES.UNASSIGNED);
  const { data: draft, isError: isDraftError } = useDraft();
  const dashboardSettings = useFetchDashboardSettings({ enabled: !!accountId })?.data;
  const isLoading = fetchingStatus;

  const isBuilding = buildDraftResponse?.status === BUILD_STATUSES.PENDING
        || buildDraftResponse?.status === BUILD_STATUSES.KG_UPDATE;

  const isBuildDisabled = !draft
        || dashboardSettings?.disablePublish
        || hasAssistantValidationErrors
        || isUpdatingCollection
        || isReadOnly
        || buildDraftResponse?.status === BUILD_STATUSES.LIVE
        || buildDraftResponse?.status === BUILD_STATUSES.NOT_FOUND
        || buildDraftResponse?.status === BUILD_STATUSES.TERMINATED
        || buildDraftResponse?.status === BUILD_STATUSES.EXPIRED
        || isBuilding
        || isLoading
        || isDraftError;

  const getBuildTooltip = () => {
    if (!isBuildDisabled) return "";
    if (!draft) return BUILD_TOOLTIP.NO_CHANGES;
    if (hasAssistantValidationErrors) return BUILD_TOOLTIP.MANDATORY_MISSING;
    if (isBuilding) return BUILD_TOOLTIP.IN_PROGRESS;

    return "";
  };

  const handleBuild = async () => {
    try {
      setFetchingStatus(true);
      const endpoint = `/assistants/${selectedAssistantId}/deploy/draft`;
      const statusResponse = await post({
        endpoint,
        data: { branchName: draft?.branchName },
        headers: { pageName }
      });
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.NOTIFICATIONS] });
      setBuildDraftResponse(statusResponse);
      setFetchingStatus(false);
      pollBuildStatus();
      const popover = unassigned.length > 0 ? DONT_SHOW_AGAIN_STORAGE.UNASSIGNED : DONT_SHOW_AGAIN_STORAGE.BUILD_DRAFT;
      setInfoPopovers(popover, true);
      deleteWidgetCookie(WIDGET_TOKEN);
    } catch (e) {
      console.error("Error occurred while building draft", e);
      setBuildDraftResponse({ status: "", url: "" });
      setDialogError(true);
      setFetchingStatus(false);
    }
  };

  return {
    isLoading,
    isBuilding,
    isBuildDisabled,
    getBuildTooltip,
    handleBuild
  };
};

export default useBuildButton;
